import type { MeSession } from "./hooks/sessionHooks";

/**
 * The wire value `CoachedSession.status` carries for a canceled occurrence
 * (`SessionStatus.CANCELED` in `api/src/data/enums.py`, serialized by `.value`).
 */
export const CANCELED_SESSION_STATUS = "canceled";

export type SessionJoinState = "canceled" | "before-gate" | "open" | "ended" | "unavailable";

export const SESSION_JOIN_CAPTIONS: Record<SessionJoinState, string | null> = {
  // Wording taken verbatim from the send-off spike (5d3ba658b,
  // `sessionSendOffState.SEND_OFF_COPY.canceled`) rather than invented a second
  // time, so the two surfaces cannot drift into two names for one state.
  canceled: "This session was canceled",
  // 🔴 NO NUMBER IN THIS STRING. It read "Opens 5 min before" until 2026-09-25, which was a
  // SECOND statement of `JOIN_WINDOW_BEFORE_START_MINUTES` that could not follow it: the
  // constant is an env var read by the guard AND by the session email, so moving it changed
  // what the email promised while this caption went on saying five. That is precisely the
  // false-promise defect the constant was extracted to close, re-created one layer out. The
  // hub says WHEN instead, from `joinOpensAt` — an instant the server derives from the same
  // helper the guard uses, so it cannot disagree with it.
  "before-gate": "Not open yet",
  open: null,
  ended: "This session has ended",
  unavailable: "Joining is unavailable",
};

/**
 * States with nothing left to join — the Join affordance must be ABSENT, not disabled.
 *
 * A disabled Join button still asserts that joining is the thing this page is for and
 * that it will become available; for a session that ended or was called off, neither is
 * true. Callers ask this instead of testing `=== "ended"`, which is what left the
 * canceled state rendering a dead button when it was added.
 */
export function hasNothingToJoin(state: SessionJoinState | null): boolean {
  return state === "ended" || state === "canceled";
}

/**
 * 🔴 THE WINDOW IS THE SERVER'S, AND THIS FUNCTION NO LONGER OWNS A COPY OF IT.
 *
 * Until 2026-09-25 this module carried `SESSION_JOIN_GATE_MS = 5 * 60 * 1000` and closed the
 * window at `endUtc`, re-deriving client-side a rule the server already enforces. Both halves
 * were wrong in the same incident:
 *
 *  * the GATE was a hardcoded five that did not read `JOIN_WINDOW_BEFORE_START_MINUTES`, so an
 *    operator raising the env var moved the guard and the email's promise and left this behind;
 *  * the CLOSE was `nowMs >= endUtc`, which returned "ended" the moment a session passed its
 *    SCHEDULED end and made the hub drop the Join button entirely — while the therapist was
 *    still sitting in the call. The server-side fix alone could not reach it: the backend would
 *    hand back a perfectly good `joinUrl` and this function would throw it away.
 *
 * Both bounds now arrive as instants (`joinOpensAt` / `joinClosesAt`), derived server-side from
 * the same helpers the guard uses. Nothing here knows a number, so nothing here can drift.
 */
export function getSessionJoinState(
  session: Pick<MeSession, "status" | "joinOpensAt" | "joinClosesAt" | "joinUrl">,
  nowMs: number,
): SessionJoinState {
  // 🔴 CANCELLATION OUTRANKS THE CLOCK, and it is checked first for that reason: a
  // canceled session is canceled whether its start is still in the future, inside the
  // live window, or already past. Derived from time alone, a cancellation with a FUTURE
  // start evaluated to `before-gate` and rendered "Join session" + an opening time
  // — identical to a live booking. That is what a client saw after canceling a session
  // and following the cancellation SMS, and it misfires for ANY route to a canceled
  // session, not just that one.
  if (session.status === CANCELED_SESSION_STATUS) {
    return "canceled";
  }
  if (nowMs < new Date(session.joinOpensAt).getTime()) {
    return "before-gate";
  }
  if (nowMs >= new Date(session.joinClosesAt).getTime()) {
    return "ended";
  }
  return session.joinUrl ? "open" : "unavailable";
}
